import { Chess, type Square } from 'chess.js';
import type { Exercise, ExerciseCheckResult } from '../../types';
import type { StockfishEngine } from '../engine/StockfishEngine';
import { scoreFromPlayerPov } from '../engine/uci';
import { computeLossCp } from '../utils/evalFormat';
import { normalizeUci } from '../utils/chessHelpers';

/**
 * Vérifie un coup tenté dans un exercice :
 *  1. Légalité (via chess.js — gère promotions et destinations).
 *  2. Si légal et différent du coup de référence, interroge le moteur pour
 *     juger si l'alternative est "suffisamment bonne" (règle explicite :
 *     perte <= toleranceCp par rapport à la référence), plutôt que
 *     d'exiger arbitrairement le seul coup attendu.
 */
export async function checkAttempt(
  exercise: Exercise,
  playedUci: string,
  engine: StockfishEngine,
): Promise<ExerciseCheckResult> {
  const chess = new Chess(exercise.fen);
  const from = playedUci.slice(0, 2);
  const to = playedUci.slice(2, 4);
  const promotion = playedUci.length > 4 ? playedUci.slice(4, 5) : undefined;

  let moveResult;
  try {
    moveResult = chess.move({ from: from as Square, to: to as Square, promotion });
  } catch {
    moveResult = null;
  }

  if (!moveResult) {
    return { legal: false, isGoalMove: false, isGoodEnough: false, error: 'Ce coup n’est pas légal dans cette position.' };
  }

  const isGoalMove = normalizeUci(playedUci) === normalizeUci(exercise.goalUci);
  if (isGoalMove) {
    return { legal: true, san: moveResult.san, isGoalMove: true, isGoodEnough: true, lossCp: 0 };
  }

  const resultAfter = await engine.analyze(chess.fen(), { depth: 14, timeoutMs: 8000 });
  const evalAfterWhite = resultAfter.isTerminal
    ? terminalScoreFor(chess.fen(), resultAfter.terminalReason)
    : (resultAfter.bestLines[0]?.score ?? { type: 'cp' as const, value: 0 });
  const evalAfterPov = scoreFromPlayerPov(evalAfterWhite, exercise.playerColor);
  const lossCp = computeLossCp({ type: 'cp', value: exercise.referenceEvalCp }, evalAfterPov);

  return {
    legal: true,
    san: moveResult.san,
    isGoalMove: false,
    isGoodEnough: lossCp <= exercise.toleranceCp,
    lossCp,
  };
}

function terminalScoreFor(fen: string, reason: 'checkmate' | 'stalemate' | 'draw' | undefined) {
  if (reason === 'checkmate') {
    const sideToMove = fen.split(' ')[1] === 'b' ? 'b' : 'w';
    const winner = sideToMove === 'w' ? 'b' : 'w';
    return { type: 'mate' as const, value: winner === 'w' ? 1 : -1 };
  }
  return { type: 'cp' as const, value: 0 };
}

/** Cases de destination légales pour une pièce donnée (pour proposer les cibles à l'UI, ex: promotions). */
export function legalDestinationsFrom(fen: string, square: string): { to: string; promotion?: string }[] {
  const chess = new Chess(fen);
  const moves = chess.moves({ square: square as Square, verbose: true });
  return moves.map((m) => ({ to: m.to, promotion: m.promotion }));
}
